import React, { useEffect, useState } from 'react';
import { CalendarClock, Trash2 } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FOLLOW_UP_FIELD, getLeadFollowUp, formatFollowUpMonth, toMonthKey } from '../../utils/leadFollowUp';

const draftFrom = (lead) => {
  const raw = lead?.[FOLLOW_UP_FIELD] || {};
  return { month: raw.month || '', date: raw.date || '', note: raw.note || '' };
};

const monthsFromNow = (n) => {
  const now = new Date();
  return toMonthKey(new Date(now.getFullYear(), now.getMonth() + n, 1));
};

const QUICK_PICKS = [
  { label: 'Next month', month: () => monthsFromNow(1) },
  { label: 'In 3 months', month: () => monthsFromNow(3) },
  { label: 'In 6 months', month: () => monthsFromNow(6) },
  { label: 'Next January', month: () => `${new Date().getFullYear() + 1}-01` },
];

const inputClass =
  'w-full px-3 py-2 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

/**
 * Lead ▸ Status: "still interested, but check back next January". Saves one top-level
 * `longTermFollowUp` map (see utils/leadFollowUp) — the Leads ▸ Follow-Up board reads it.
 */
export default function LeadLongTermFollowUpCard({ lead, leadId, currentUser, onSaved }) {
  const [draft, setDraft] = useState(() => draftFrom(lead));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const saved = getLeadFollowUp(lead);
  const savedDraft = draftFrom(lead);
  const dirty = draft.month !== savedDraft.month || draft.date !== savedDraft.date || draft.note !== savedDraft.note;

  // Another tab/user saved — pick it up unless there are unsaved edits here.
  useEffect(() => {
    if (!dirty) setDraft(draftFrom(lead));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.[FOLLOW_UP_FIELD]]);

  const setMonth = (month) =>
    setDraft((d) => ({ ...d, month, date: d.date && d.date.slice(0, 7) !== month ? '' : d.date }));

  const setDate = (date) => setDraft((d) => ({ ...d, date, month: date ? date.slice(0, 7) : d.month }));

  const write = async (value) => {
    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'accounts', leadId), { [FOLLOW_UP_FIELD]: value });
      onSaved?.(value);
    } catch (err) {
      console.error('Error saving long-term follow-up:', err);
      setError('Could not save the follow-up. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!draft.month) return;
    write({
      month: draft.month,
      date: draft.date || null,
      note: draft.note.trim(),
      updatedAt: new Date().toISOString(),
      updatedBy: currentUser?.displayName || currentUser?.email || null,
    });
  };

  const handleClear = async () => {
    await write(null);
    setDraft({ month: '', date: '', note: '' });
  };

  const [minDate, maxDate] = draft.month
    ? (() => {
        const [y, m] = draft.month.split('-').map(Number);
        const last = new Date(y, m, 0).getDate();
        return [`${draft.month}-01`, `${draft.month}-${String(last).padStart(2, '0')}`];
      })()
    : [undefined, undefined];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">Long-Term Follow-Up</h2>
      <p className="mt-1 mb-5 text-sm text-gray-500 dark:text-gray-400 text-center">
        For leads who want to revisit later. Shows on Leads ▸ Follow-Up.
      </p>

      {saved && (
        <div className="flex justify-center mb-5">
          <span
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold ${
              saved.overdue
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
            }`}
          >
            <CalendarClock className="w-4 h-4" />
            Follow up {saved.label}
            {saved.overdue && ' · overdue'}
          </span>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {QUICK_PICKS.map((pick) => {
          const month = pick.month();
          const active = draft.month === month;
          return (
            <button
              key={pick.label}
              type="button"
              onClick={() => setMonth(month)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                active
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={formatFollowUpMonth(month, { short: false })}
            >
              {pick.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">Follow-up month</label>
          <input type="month" value={draft.month} onChange={(e) => setMonth(e.target.value)} className={inputClass} />
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">
            Exact date <span className="font-normal">(optional)</span>
          </label>
          <input
            type="date"
            value={draft.date}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">Note</label>
          <textarea
            value={draft.note}
            onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            rows={2}
            placeholder="e.g. Wants to revisit after the busy season"
            className={`${inputClass} resize-none`}
          />
        </div>
      </div>

      {error && <p className="mt-3 text-center text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-5 flex justify-center gap-3">
        {saved && (
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !draft.month || !dirty}
          className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : saved ? 'Update Follow-Up' : 'Save Follow-Up'}
        </button>
      </div>
    </div>
  );
}
